import { format } from 'date-fns'

import {
  Button,
  IconButton,
  ChevronLeftIcon,
  ChevronRightIcon,
  Flex,
  Text,
  ChevronDownIcon,
  Heading,
  Select,
} from '@buffer-mono/popcorn'

import type { WeekAnchor } from '~publish/helpers/temporal'
import { useTimezone } from '~publish/hooks/useTimezone'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'

import { useCalendar } from '../../../components/Calendar/hooks/useCalendar'
import { isCalendarViewMode } from '../../../components/Calendar/hooks/useCalendarDateRangeViewMode'

import styles from './CalendarControls.module.css'

import type { JSX } from 'react'

export function DesktopCalendarControls({
  className,
  weekStartsOn,
  weekAnchor,
}: {
  className?: string
  weekStartsOn?: 0 | 1
  weekAnchor?: WeekAnchor
}): JSX.Element {
  const timezone = useTimezone()
  const dayToStartWeek = useWeekStartsOn()
  const {
    navigateToToday,
    navigateToPrevious,
    navigateToNext,
    toggleViewMode,
    viewMode,
    selectedDate,
    startDate,
    endDate,
  } = useCalendar({
    timezone,
    weekStartsOn: weekStartsOn ?? (dayToStartWeek === 'monday' ? 1 : 0),
    weekAnchor,
  })

  const navigationLabel =
    viewMode === 'week' ? 'Week' : viewMode === 'three-day' ? '3 Days' : 'Month'

  let dateString = format(
    selectedDate.toPlainDateTime().toString(),
    'MMMM yyyy',
  )
  if (
    startDate.month !== endDate.month &&
    (viewMode === 'week' || viewMode === 'three-day')
  ) {
    dateString = `${format(
      startDate.toPlainDateTime().toString(),
      'MMM',
    )}-${format(endDate.toPlainDateTime().toString(), 'MMM')} ${format(
      startDate.toPlainDateTime().toString(),
      'yyyy',
    )}`
  }

  return (
    <Flex gap="md" align="center" className={className}>
      <Flex align="center">
        <IconButton
          label={`Previous ${navigationLabel}`}
          variant="tertiary"
          onClick={navigateToPrevious}
          className={styles.navButton}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          label={`Next ${navigationLabel}`}
          variant="tertiary"
          onClick={navigateToNext}
        >
          <ChevronRightIcon />
        </IconButton>
        <Heading as="h2" size="sm" className={styles.dateHeader}>
          {dateString}
        </Heading>
      </Flex>
      <Button variant="secondary" size="medium" onClick={navigateToToday}>
        Today
      </Button>
      <Select
        value={viewMode === 'three-day' ? 'week' : viewMode}
        onValueChange={(mode): void => {
          if (isCalendarViewMode(mode)) {
            toggleViewMode(mode)
          }
        }}
      >
        <Select.Trigger aria-label="View">
          <Button variant="tertiary" size="medium">
            {viewMode === 'month' ? 'Month' : 'Week'}
            <ChevronDownIcon />
          </Button>
        </Select.Trigger>
        <Select.Content>
          <Select.Item value={'week'}>
            <Text weight="medium">Week</Text>
          </Select.Item>
          <Select.Item value={'month'}>
            <Text weight="medium">Month</Text>
          </Select.Item>
        </Select.Content>
      </Select>
    </Flex>
  )
}
